import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  fetchProjectDetail,
  updateProject,
  deleteProject,
  clearProjectDetail,
  fetchProjectResources,
  fetchProjectCollaborators,
  fetchProjectDashboard,
  PROJECT_DETAIL_FORBIDDEN,
} from '../../store/actions/Projects/ProjectActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import { buildProjectCollaboratorAdapter } from '../../utils/collaboratorAdapters/project';
import DeleteProjectConfirmation from './DeleteProjectConfirmation';
import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Input from '../shared/TailwindComponents/Input';
import DateTimePicker from '../shared/TailwindComponents/DatePicker';
import Textarea from '../shared/TailwindComponents/Textarea';
import ProfileImage from '../Header/ProfileImage';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { formatDateTime } from '../../utils/functions';
import {
  PiPencilSimpleLight,
  PiTrashLight,
  PiUsersThreeLight,
  PiLinkSimpleLight,
  PiClockCounterClockwiseLight,
  PiCalendarBlankLight,
  PiFlagLight,
  PiCircleDashedLight,
  PiTextAlignLeftLight,
  PiPlusLight,
  PiBriefcaseLight,
  PiCheckSquareOffsetLight,
  PiTableLight,
  PiFileMagnifyingGlassLight,
  PiBookOpenTextLight,
  PiDatabaseLight,
  PiFileTextLight,
  PiFileLight,
  PiClockCountdownLight,
  PiLinkSimpleHorizontalLight,
  PiProjectorScreenChartLight,
  PiLockKeyLight,
} from 'react-icons/pi';
import ProjectResourcesTab from './ProjectResourcesTab';
import { FeatureTour, projectDetailOrientationSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';
// import ProjectMilestones from './ProjectMilestones';
import ProjectFeed from './ProjectFeed';
// import ProjectDashboard from './ProjectDashboard';

// ─── Config ─────────────────────────────────────────────────────

const statusColorMap: Record<string, BadgeColor> = { active: 'green', archived: 'gray', completed: 'blue' };
const priorityColorMap: Record<string, BadgeColor> = { low: 'gray', medium: 'yellow', high: 'orange', urgent: 'red' };

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'completed', label: 'Completed' },
];
const priorityOptions = [
  { value: '', label: 'None' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const formatRelativeDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, urgency: 'overdue' as const };
  if (days <= 3) return { text: `${days}d remaining`, urgency: 'soon' as const };
  if (days <= 14) return { text: `${days}d remaining`, urgency: 'upcoming' as const };
  return { text: formatDateTime(iso), urgency: 'normal' as const };
};

// ─── Skeleton Loader ────────────────────────────────────────────

const DetailSkeleton: React.FC = () => (
  <div className="px-4 py-5">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-5">
          <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade mb-4" />
          <div className="space-y-2">
            <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
            <div className="h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade mb-3" />
              <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-5">
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-5">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade mb-4" />
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ─── Component ──────────────────────────────────────────────────

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { currentProject, isLoadingDetail, dashboard, resources } = useSelector((state: RootState) => state.projects);

  const [activeTab, setActiveTab] = useState('Details');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState({ title: '', description: '', status: 'active', priority: '', due_date: '' });
  const [showAuditLog, setShowAuditLog] = useState(false);

  const {
    isRunning: isProjectDetailTourRunning,
    handleJoyrideCallback: handleProjectDetailTourCallback,
  } = useFeatureTour({
    tourId: `project-detail-orientation-${projectId}`,
    steps: projectDetailOrientationSteps,
    readyToShow: !!currentProject,
    autoStartDelay: 700,
  });

  useEffect(() => {
    let cancelled = false;
    if (projectId) {
      // Fetch detail first; if the user has no access (403/404), redirect to
      // the projects list before kicking off the sub-resource fetches that
      // would otherwise produce noisy 403 toasts.
      (async () => {
        const result = await dispatch(fetchProjectDetail(projectId));
        if (cancelled) return;
        if (result === PROJECT_DETAIL_FORBIDDEN) {
          history.replace('/projects');
          return;
        }
        if (!result) return;
        dispatch(fetchProjectResources(projectId));
        dispatch(fetchProjectCollaborators(projectId));
        dispatch(fetchProjectDashboard(projectId));
      })();
    }
    return () => { cancelled = true; dispatch(clearProjectDetail()); };
  }, [dispatch, projectId, history]);

  useEffect(() => {
    if (currentProject) {
      setEditData({
        title: currentProject.title || '',
        description: currentProject.description || '',
        status: currentProject.status || 'active',
        priority: currentProject.priority || '',
        due_date: currentProject.due_date || '',
      });
    }
  }, [currentProject]);

  const handleSaveEdit = async () => {
    if (!projectId) return;
    setIsSaving(true);
    try {
      const result = await dispatch(updateProject(projectId, editData));
      if (result) {
        setIsEditing(false);
        dispatch(fetchProjectDetail(projectId));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t('Delete Project'),
      subMsg: t('This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
      width: '500px',
      content: (
        <DeleteProjectConfirmation
          projectName={currentProject?.title}
          onConfirm={async () => {
            await dispatch(deleteProject(projectId!));
            dispatch(hideModal());
            history.push('/projects');
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => {},
    }));
  };

  const handleManageCollaborators = () => {
    const adapter = buildProjectCollaboratorAdapter(dispatch, {
      projectUuid: projectId!,
      collaborators: currentProject?.shared_with || [],
      currentUserUuid: currentProject?.owner?.user_id ? String(currentProject.owner.user_id) : undefined,
      canAdmin,
      onUpdate: () => {
        dispatch(fetchProjectDetail(projectId!));
        dispatch(fetchProjectCollaborators(projectId!));
      },
      subMessage: canAdmin
        ? t('Add, remove or change access for people on this project.')
        : t('View project access and permission levels.'),
    });
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      width: '50%',
      message: canAdmin ? t('Manage Access') : t('Access'),
      icon: <PiUsersThreeLight className="w-5 h-5" />,
      onCancel: () => dispatch(hideSidePanel()),
      ...adapter,
    } as any));
  };

  const handleTabClick = useCallback((tabValue: string) => { setActiveTab(tabValue); }, []);

  const handleOpenResourcePanel = () => {
    dispatch(showSidePanel({
      type: 'projectResources', showSidePanel: true, width: '70%',
      projectUuid: projectId!,
      message: t('Link Resources'),
      subMsg: t('Search and link resources to this project.'),
      icon: <PiLinkSimpleHorizontalLight className="w-5 h-5 text-gray-600 dark:text-gray-400" />,
      onResourceLinked: () => { dispatch(fetchProjectResources(projectId!)); dispatch(fetchProjectDetail(projectId!)); },
    } as any));
  };

  // ─── Loading ──────────────────────────────────────────────────

  if (isLoadingDetail && !currentProject) {
    return (
      <div className="flex flex-col h-full">
        <Header breadcrumbs={[{ name: t('Projects'), href: '/projects', current: false }, { name: '...', href: '#', current: true }]} />
        <div>
          <FilterBarWithTabs tabs={[{ label: t('Details'), value: 'Details' }, { label: t('Resources'), value: 'Resources' }]} activeTab="Details" onTabClick={() => {}} />
        </div>
        <DetailSkeleton />
      </div>
    );
  }

  if (!currentProject) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <PiProjectorScreenChartLight className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm">{t('Project not found')}</p>
        <Button variant="outline" size="small" onClick={() => history.push('/projects')} className="mt-3">{t('Back to Projects')}</Button>
      </div>
    );
  }

  const myAccessLevel: string = (currentProject as any).my_access_level || 'view';
  const canEdit = myAccessLevel === 'edit' || myAccessLevel === 'admin';
  const canAdmin = myAccessLevel === 'admin';

  const tabs = [
    { label: t('Details'), value: 'Details' },
    { label: t('Resources'), value: 'Resources' },
    { label: t('Feed'), value: 'Feed' },
  ];

  const dropdownActions = [
    ...(canEdit ? [{ label: t('Edit'), onClick: () => { setActiveTab('Details'); setIsEditing(true); }, icon: <PiPencilSimpleLight className="w-4 h-4" /> }] : []),
    ...(canAdmin ? [{ label: t('Manage Access'), onClick: handleManageCollaborators, icon: <PiUsersThreeLight className="w-4 h-4" /> }] : []),
    { label: t('Activity Log'), onClick: () => setShowAuditLog((prev) => !prev), icon: <PiClockCounterClockwiseLight className="w-4 h-4" /> },
    ...(canAdmin ? [{ label: t('Delete'), onClick: handleDelete, icon: <PiTrashLight className="w-4 h-4" />, variant: 'destructive' as const }] : []),
  ];

  const dueDateInfo = formatRelativeDate(currentProject.due_date);

  return (
    <div className="flex flex-col h-full relative">
      <div data-tour-id="project-header">
        <Header
          breadcrumbs={[
            { name: t('Projects'), href: '/projects', current: false },
            { name: currentProject.title || t('Untitled'), href: '#', current: true },
          ]}
          actions={canEdit ? [{ label: t('Link Resource'), onClick: handleOpenResourcePanel, icon: <PiPlusLight className="w-4 h-4" />, variant: 'primary' as const, tourId: 'project-link-resource' }] : []}
          dropdownActions={dropdownActions}
          dropdownTourId="project-actions-menu"
        />
      </div>
      <div data-tour-id="project-tabs">
        <FilterBarWithTabs tabs={tabs} activeTab={activeTab} onTabClick={handleTabClick} />
      </div>

      <div className="flex-1">
        {activeTab === 'Details' && (
          <div className="max-h-[calc(100vh-95px)] overflow-y-auto custom-scrollbar-main px-4 py-5">
            {/* Dashboard cards - commented out for now */}
            {/* <ProjectDashboard projectUuid={projectId!} /> */}

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              <div className="lg:col-span-3 space-y-5">
                {/* Description card */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                  <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                      <PiTextAlignLeftLight className="h-4 w-4" />
                      <span className="text-xs font-medium">{t('Description')}</span>
                    </div>
                    {!isEditing && canEdit && (
                      <Button variant="outline" size="small" onClick={() => setIsEditing(true)}>
                        <PiPencilSimpleLight className="h-3.5 w-3.5 mr-1" />{t('Edit')}
                      </Button>
                    )}
                  </div>
                  <div className="p-5">
                    {isEditing ? (
                      <div className="space-y-4">
                        <Input id="project-title" type="text" label={t('Title')} value={editData.title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditData({ ...editData, title: e.target.value })} />
                        <Textarea id="project-description" label={t('Description')} value={editData.description} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditData({ ...editData, description: e.target.value })} rows={4} />
                        <div className="grid grid-cols-3 gap-3">
                          <Dropdown label={t('Status')} options={statusOptions} value={editData.status} onChange={(v) => setEditData({ ...editData, status: Array.isArray(v) ? v[0] : v })} />
                          <Dropdown label={t('Priority')} options={priorityOptions} value={editData.priority} onChange={(v) => setEditData({ ...editData, priority: Array.isArray(v) ? v[0] : v })} />
                          <DateTimePicker
                            id="project-due-date"
                            label={t('Due Date')}
                            selected={editData.due_date ? new Date(editData.due_date) : null}
                            onChange={(date) => setEditData({ ...editData, due_date: date ? date.toISOString() : '' })}
                            showTimeSelect
                            fullWidth
                          />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <Button variant="primary" size="small" onClick={handleSaveEdit} disabled={isSaving}>{isSaving ? t('Saving...') : t('Save')}</Button>
                          <Button variant="neutral" size="small" onClick={() => setIsEditing(false)}>{t('Cancel')}</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed max-w-prose">
                        {currentProject.description || <span className="text-gray-400 italic">{t('No description yet. Click edit to add one.')}</span>}
                      </p>
                    )}
                  </div>
                </div>

                {/* Metadata grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                    <div className="flex items-center gap-1.5 mb-2"><PiCircleDashedLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" /><span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Status')}</span></div>
                    <Badge color={statusColorMap[currentProject.status] || 'gray'}>{currentProject.status ? currentProject.status.charAt(0).toUpperCase() + currentProject.status.slice(1) : t('Active')}</Badge>
                  </div>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                    <div className="flex items-center gap-1.5 mb-2"><PiFlagLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" /><span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Priority')}</span></div>
                    {currentProject.priority ? <Badge color={priorityColorMap[currentProject.priority] || 'gray'}>{currentProject.priority.charAt(0).toUpperCase() + currentProject.priority.slice(1)}</Badge> : <p className="text-sm text-gray-400 dark:text-gray-500">{t('Not set')}</p>}
                  </div>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                    <div className="flex items-center gap-1.5 mb-2"><PiCalendarBlankLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" /><span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Created')}</span></div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{formatDateTime(currentProject.created_at) || '-'}</p>
                  </div>
                  <div className={clsx('border rounded-lg shadow-sm p-4', dueDateInfo?.urgency === 'overdue' ? 'border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10' : dueDateInfo?.urgency === 'soon' ? 'border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800')}>
                    <div className="flex items-center gap-1.5 mb-2"><PiClockCountdownLight className={clsx('h-3.5 w-3.5', dueDateInfo?.urgency === 'overdue' ? 'text-red-400' : dueDateInfo?.urgency === 'soon' ? 'text-amber-400' : 'text-gray-400 dark:text-gray-500')} /><span className={clsx('text-[11px] font-medium', dueDateInfo?.urgency === 'overdue' ? 'text-red-400' : dueDateInfo?.urgency === 'soon' ? 'text-amber-400' : 'text-gray-400 dark:text-gray-500')}>{t('Due Date')}</span></div>
                    <div className="flex flex-col gap-0.5">
                      <p className={clsx('text-sm font-medium', dueDateInfo?.urgency === 'overdue' ? 'text-red-600 dark:text-red-400' : dueDateInfo?.urgency === 'soon' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-200')}>{currentProject.due_date ? formatDateTime(currentProject.due_date) : t('Not set')}</p>
                      {dueDateInfo && dueDateInfo.urgency !== 'normal' && <span className={clsx('text-[11px] font-medium', dueDateInfo.urgency === 'overdue' && 'text-red-500 dark:text-red-400', dueDateInfo.urgency === 'soon' && 'text-amber-500 dark:text-amber-400', dueDateInfo.urgency === 'upcoming' && 'text-yellow-600 dark:text-yellow-400')}>{dueDateInfo.text}</span>}
                    </div>
                  </div>
                </div>

                {/* Activity row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentProject.owner && (
                    <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
                      <ProfileImage profilePicture={currentProject.owner.profile_picture || null} email={currentProject.owner.email || ''} initials={currentProject.owner.initials || '??'} name={currentProject.owner.name} size="w-8 h-8" />
                      <div className="min-w-0">
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Owner')}</span>
                        <p className="text-sm text-gray-700 dark:text-gray-200 truncate">{currentProject.owner.name}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3 transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30" onClick={handleManageCollaborators}>
                    <PiUsersThreeLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Access')}</span>
                      <p className="text-sm text-gray-700 dark:text-gray-200">{currentProject.shared_with?.length || 0} {t('members')}</p>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                {/* {dashboard?.recent_activity && dashboard.recent_activity.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                      <PiClockCounterClockwiseLight className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('Recent Activity')}</span>
                    </div>
                    <div className="divide-y divide-gray-50 dark:divide-gray-700/40">
                      {dashboard.recent_activity.map((entry: any, i: number) => (
                        <div key={entry.id || i} className="flex items-center justify-between px-5 py-2.5">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Badge color={
                              ({ create: 'green', link: 'teal', unlink: 'amber', collaborator_add: 'emerald', collaborator_remove: 'amber', collaborator_access_changed: 'sky', grant_access: 'sky', milestone_added: 'teal', milestone_completed: 'green', milestone_deleted: 'amber', feed_post: 'slate', feed_comment: 'slate' } as Record<string, any>)[entry.action] || 'slate'
                            }>
                              {({ create: 'Created', link: 'Linked', unlink: 'Unlinked', collaborator_add: 'Added', collaborator_remove: 'Removed', collaborator_access_changed: 'Access Changed', grant_access: 'Access Granted', access_request_approve: 'Approved', access_request_deny: 'Denied', milestone_added: 'Milestone', milestone_completed: 'Completed', milestone_updated: 'Updated', milestone_deleted: 'Deleted', milestones_reordered: 'Reordered', feed_post: 'Posted', feed_comment: 'Commented', update: 'Updated', delete: 'Deleted' } as Record<string, string>)[entry.action] || (entry.action || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                            </Badge>
                            <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {(entry.details || '').replace(/_/g, ' ')}
                            </span>
                          </div>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">{formatDateTime(entry.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )} */}
              </div>

              {/* Right column */}
              <div className="lg:col-span-2 space-y-5">
                {/* Milestones - commented out for now */}
                {/* <ProjectMilestones projectUuid={projectId!} /> */}

                {/* Linked Resources summary */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                  <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400"><PiLinkSimpleLight className="h-4 w-4" /><span className="text-xs font-medium">{t('Linked Resources')}</span></div>
                    {canEdit && <Button variant="outline" size="small" onClick={handleOpenResourcePanel}><PiPlusLight className="h-3.5 w-3.5 mr-1" />{t('Link')}</Button>}
                  </div>
                  <div className="p-4 space-y-2">
                    {currentProject.total_resources > 0 ? (
                      <>
                        {currentProject.resource_counts && Object.entries(currentProject.resource_counts).map(([type, count]) => {
                          const icons: Record<string, React.ReactElement> = { matter: <PiBriefcaseLight className="w-4 h-4 text-blue-500" />, task: <PiCheckSquareOffsetLight className="w-4 h-4 text-green-500" />, table: <PiTableLight className="w-4 h-4 text-teal-500" />, review: <PiBookOpenTextLight className="w-4 h-4 text-emerald-500" />, playbook: <PiBookOpenTextLight className="w-4 h-4 text-emerald-500" />, review_session: <PiFileMagnifyingGlassLight className="w-4 h-4 text-sky-500" />, datasource: <PiDatabaseLight className="w-4 h-4 text-cyan-500" />, template: <PiFileTextLight className="w-4 h-4 text-slate-500" /> };
                          const labels: Record<string, string> = { matter: 'Matters', task: 'Tasks', table: 'Tables', review: 'Playbooks', playbook: 'Playbooks', review_session: 'Reviews', datasource: 'Data Sources', template: 'Templates' };
                          return (<div key={type} className="flex items-center justify-between py-1.5"><div className="flex items-center gap-2">{icons[type] || <PiFileLight className="w-4 h-4 text-gray-400" />}<span className="text-sm text-gray-700 dark:text-gray-300">{labels[type] || type}</span></div><span className="text-sm font-medium text-gray-900 dark:text-white">{count as number}</span></div>);
                        })}
                        <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-700/60 flex justify-between">
                          <span className="text-xs font-medium text-gray-500">{t('Total')}</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-gray-900 dark:text-white">{currentProject.total_resources}</span>
                            {(resources?.restricted_count ?? 0) > 0 && (
                              <span className="flex items-center gap-0.5 text-[10px] text-gray-400 dark:text-gray-500">
                                <PiLockKeyLight className="w-3 h-3" />
                                {resources?.restricted_count} {t('restricted')}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    ) : <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">{t('No resources linked yet.')}</p>}
                  </div>
                </div>

                {currentProject.shared_with && currentProject.shared_with.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                        <PiUsersThreeLight className="h-4 w-4" />
                        <span className="text-xs font-medium">{t('Access')}</span>
                      </div>
                      <Button variant="outline" size="small" onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleManageCollaborators(); }}>
                        {t('Manage')}
                      </Button>
                    </div>
                    <div className="p-4 space-y-2.5">
                      {currentProject.shared_with.slice(0, 6).map((collab: any, i: number) => (
                        <div key={collab.user_id || i} className="flex items-center gap-2.5">
                          <ProfileImage profilePicture={collab.profile_picture || null} email={collab.email || ''} initials={collab.initials || '??'} name={collab.name} size="w-6 h-6" />
                          <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{collab.name || collab.email}</span>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 capitalize">{collab.role === 'creator' || collab.access === 'admin' ? t('Owner') : collab.access}</span>
                        </div>
                      ))}
                      {currentProject.shared_with.length > 6 && <button onClick={handleManageCollaborators} className="text-xs text-primary-600 dark:text-primary-400 hover:underline pt-1">+{currentProject.shared_with.length - 6} {t('more')}</button>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Feed' && <ProjectFeed projectUuid={projectId!} />}

        {activeTab === 'Resources' && <ProjectResourcesTab projectUuid={projectId!} onOpenLinkPanel={handleOpenResourcePanel} />}
      </div>

      {/* Audit Log Side Panel */}
      {showAuditLog && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300" onClick={() => setShowAuditLog(false)} />
          <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
            <AuditLogPanel entityType="project" entityId={projectId!} entityName={currentProject.title || ''} onClose={() => setShowAuditLog(false)} />
          </div>
        </>
      )}

      <FeatureTour
        steps={projectDetailOrientationSteps}
        run={isProjectDetailTourRunning}
        onCallback={handleProjectDetailTourCallback}
        continuous
      />
    </div>
  );
};

export default ProjectDetail;
